import CurriculumDownloadModal from '@components/home/curriculum-download-modal'
import { Trans, defineMessage } from '@lingui/macro'
import { useState } from 'react'
import CourseCard from '@components/locale-pages/home/course-card'

const HOVER_BG = [
  'hover:bg-orange-100 hover:border-orange',
  'hover:bg-orange2-50 hover:border-orange2-400 hover:bg-opacity-75',
  'hover:bg-grey-200 hover:border-grey',
  'hover:bg-blue-50 hover:border-blue-300 hover:bg-opacity-50',
  'hover:bg-red-50 hover:border-red-200',
  'hover:bg-yellow-100 hover:bg-opacity-50 hover:border-yellow-200',
  'hover:bg-violet-50 hover:border-violet-100',
  'hover:bg-pink hover:border-pink',
]

const COURSE_PAGE_URL = {
  codingGrandMaster: '/courses/coding-grandmaster/',
  codingProdigy: '/courses/coding-prodigy/',
  codingchampion: '/courses/coding-champion/',
  scratchPrograming: '/courses/scratch-programming/',
  pythonForKids: '/courses/python-for-kids/',
  gameDevelopment: '/courses/game-development/',
  webDevelopment: 'https://www.codingal.com/courses/web-development/',
  appDevelopment: 'https://www.codingal.com/courses/app-development/',
  dataScience: 'https://www.codingal.com/courses/data-science/',
  roblox: 'https://www.codingal.com/courses/roblox-coding/',
  APComputerScienceA: 'https://www.codingal.com/courses/ap-computer-science-a/',
  IoiAlgorithms: 'https://www.codingal.com/courses/ioi-algorithms-course/',
  aiAndMl: 'https://www.codingal.com/courses/ai-and-machine-learning/',
}

export type CourseInfoType = {
  title: JSX.Element
  titleCompactView: JSX.Element
  grade: string
  activites: number
  lessons: number
  duration: string
  Age: string
  description: any
  image: string
  altText: string
  outcomes: any[]
  courseCode: string
  curriculumPDFLink: string
  trialCourseQuery: string
  isBestSeller: boolean
  coursePageUrl: string
  gradeOptions?: number[]
}

export const COURSE_INFO: CourseInfoType[] = [
  {
    title: (
      <Trans>
        Coding Grandmaster:
        <br />
        Master Coding Skills
      </Trans>
    ),
    titleCompactView: <Trans>Coding Grand: Master Coding Skills</Trans>,
    grade: '1-10',
    activites: 350,
    lessons: 144,
    duration: '12-18',
    Age: '5-15',
    description: defineMessage({
      message: `Embark on an amazing learning journey with 144 lessons for kids and teens to master coding. Choose from a pre-defined curriculum or customize your own.`,
    }),
    image: 'coding-grandmaster',
    altText: 'Online Coding Course for Kids & Teens to Master Coding Skills',
    outcomes: [
      defineMessage({ message: 'Learn to build apps, games & websites' }),
      defineMessage({ message: 'Mastery in all coding skills' }),
      defineMessage({ message: 'Project based learning' }),
      defineMessage({
        message: 'Coding Grandmaster certificate, accredited by STEM.org',
      }),
    ],
    courseCode: 'cgm',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: true,
    coursePageUrl: COURSE_PAGE_URL.codingGrandMaster,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  },
  {
    title: (
      <Trans>
        Coding Prodigy:
        <br />
        Code Like a Pro
      </Trans>
    ),
    titleCompactView: <Trans>Coding Prodigy: Code Like a Pro</Trans>,
    grade: '1-12',
    activites: 184,
    lessons: 96,
    duration: '9-12',
    Age: '5-17',
    description: defineMessage({
      message:
        'A 96-lesson coding curriculum designed for kids and teens to master coding skills, fostering creativity and technical proficiency.',
    }),
    image: 'coding-prodigy',
    altText: 'Online Coding Course for Kids & Teens to Code Like a Pro',
    outcomes: [
      defineMessage({ message: 'Learn to code like a pro' }),
      defineMessage({ message: 'Create amazing apps and games' }),
      defineMessage({
        message: 'Earn Coding Prodigy certificate (STEM.org Accredited)',
      }),
      defineMessage({ message: 'Master the art of problem solving' }),
    ],
    courseCode: 'cp',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.codingProdigy,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  },
  {
    title: (
      <Trans>
        Coding Champion:
        <br />
        Learn to Code
      </Trans>
    ),
    titleCompactView: <Trans>Coding Champion: Learn to Code</Trans>,
    grade: '1-12',
    activites: 50,
    lessons: 48,
    duration: '4-6',
    Age: '5-17',
    description: defineMessage({
      message:
        'Kickstart coding for kids and teens with this fun course! Enjoy 48 exciting lessons and hands-on projects that spark creativity and build skills.',
    }),
    image: 'coding-champion',
    altText: 'Coding Course for Kids & Teens to Learn to Code',
    outcomes: [
      defineMessage({ message: 'Kickstart your coding journey' }),
      defineMessage({ message: 'Build Awesome Apps, Games & Websites' }),
      defineMessage({
        message: 'Earn Coding Champion Certificate (STEM.org Accredited)',
      }),
      defineMessage({ message: 'Unlock Problem-Solving Superpowers' }),
    ],
    courseCode: 'cc',
    curriculumPDFLink: '',
    trialCourseQuery: 'coding',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.codingchampion,
    gradeOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  },
  {
    title: (
      <Trans>
        Scratch Programming:
        <br />
        Unleash Your Creativity
      </Trans>
    ),
    titleCompactView: (
      <Trans>Scratch Programming: Unleash Your Creativity</Trans>
    ),
    grade: '1-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '5-14',
    description: defineMessage({
      message:
        'Learn coding with Scratch: build games, animate, dive into AI, and unleash your creativity with endless possibilities.',
    }),
    image: 'scratch-programming',
    altText: 'Online Scratch Programming Course for Kids & Teens',
    outcomes: [
      defineMessage({ message: 'Learn the Coding Language' }),
      defineMessage({ message: 'Make Stuff Move (Animations & Games)' }),
      defineMessage({ message: 'Meet the Super-Smart Machines (AI Basics)' }),
      defineMessage({ message: 'Design Interactive Stories and Art' }),
    ],
    courseCode: 'sp',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/codingal-scratch-curriculum.pdf',
    trialCourseQuery: 'scratch-programming-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.scratchPrograming,
  },
  {
    title: <Trans>Game Development for Kids: Build Fun Games</Trans>,
    titleCompactView: <Trans>Game Development for Kids: Build Fun Games</Trans>,
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'Level up your game dev skills with Scratch and Code.org! Create epic games and unleash your creativity effortlessly.',
    }),
    image: 'game-development',
    altText: 'Online Game Development Course for Kids to Build Fun Games',
    outcomes: [
      defineMessage({ message: 'Code Like a Pro (For Games)' }),
      defineMessage({
        message: 'Become a Game Creator (Scratch & Javascript)',
      }),
      defineMessage({ message: 'Think Like a Gamer (But Smarter)' }),
      defineMessage({
        message: 'Animate Characters and Objects (Bringing Games to Life)',
      }),
    ],
    courseCode: 'gd',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/game-development.pdf',
    trialCourseQuery: 'game-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.gameDevelopment,
  },
  {
    title: <Trans>App Development for Kids: Create AI-Powered Apps</Trans>,
    titleCompactView: (
      <Trans>App Development for Kids: Create AI-Powered Apps</Trans>
    ),
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'A hands-on course where kids learn the art of app development and bring their unique ideas to life for the world to see!',
    }),
    image: 'app-development',
    altText: 'Online App Development Course for Kids to Create AI-Powered Apps',
    outcomes: [
      defineMessage({ message: 'Build Interactive Interfaces' }),
      defineMessage({ message: 'Design Awesome Apps' }),
      defineMessage({
        message:
          'Make Your App Super Smart (Integrate camera, accelerometers, GPS, etc.)',
      }),
      defineMessage({ message: 'Share Your App with the World' }),
    ],
    courseCode: 'ad',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/app-development.pdf',
    trialCourseQuery: 'app-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.appDevelopment,
  },
  {
    title: <Trans>Roblox Champion: Build & Design Epic Games</Trans>,
    titleCompactView: <Trans>Roblox Champion: Build & Design Epic Games</Trans>,
    grade: '4-8',
    activites: 50,
    lessons: 36,
    duration: '2-4',
    Age: '8-14',
    description: defineMessage({
      message:
        'Create and launch Roblox games with expert tutors. Turn your ideas into viral hits!',
    }),
    image: 'roblox-champion',
    altText: 'Online Roblox Course for Kids to Build & Design Epic Games',
    outcomes: [
      defineMessage({ message: 'Become a Roblox Game Builder' }),
      defineMessage({ message: 'Learn Scripting with Lua' }),
      defineMessage({
        message:
          'Design Fun & Different 3D Games (tycoon simulations, racing games, and horror experiences)',
      }),
      defineMessage({ message: 'Share Your Games with the World' }),
    ],
    courseCode: 'rc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/roblox-curriculum.pdf',
    trialCourseQuery: 'roblox-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.roblox,
  },
  {
    title: <Trans>Website Development: Learn to Build Websites</Trans>,
    titleCompactView: (
      <Trans>Website Development: Learn to Build Websites</Trans>
    ),
    grade: '8-12',
    activites: 90,
    lessons: 90,
    duration: '10-12',
    Age: '13-17',
    description: defineMessage({
      message:
        'Learn the fundamentals of the web and enhance your skills in building Interactive web pages using HTML, CSS, JavaScript, and more.',
    }),
    image: 'web-development',
    altText:
      'Website Development Course for Teens to Learn to Build and deploy Websites',
    outcomes: [
      defineMessage({ message: 'Build responsive websites' }),
      defineMessage({ message: 'Develop interactive web pages' }),
      defineMessage({ message: 'Master website development' }),
      defineMessage({ message: 'Implement best practices in web dev' }),
    ],
    courseCode: 'wd',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/web-development.pdf',
    trialCourseQuery: 'web-development-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.webDevelopment,
  },
  {
    title: <Trans>Python Champion: Enhance Your Skills</Trans>,
    titleCompactView: <Trans>Python Champion: Enhance Your Skills</Trans>,
    grade: '6-12',
    activites: 90,
    lessons: 44,
    duration: '4-6',
    Age: '11-17',
    description: defineMessage({
      message:
        'Learn Python through project-based learning, exploring fundamentals, advanced libraries, OOP, game development, GUIs, and more.',
    }),
    image: 'python-champion',
    altText: 'Online Python Course for Teens to Enhance Coding Skills',
    outcomes: [
      defineMessage({ message: 'Master Python programming' }),
      defineMessage({ message: 'Build interactive games' }),
      defineMessage({ message: 'Develop GUI applications' }),
      defineMessage({ message: 'Understand & apply OOP concepts' }),
    ],
    courseCode: 'pc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/codingal-python-curriculum.pdf',
    trialCourseQuery: 'python-for-kids-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.pythonForKids,
  },
  {
    title: <Trans>Data Science for Teens: Learn Python & Analytics</Trans>,
    titleCompactView: (
      <Trans>Data Science for Teens: Learn Python & Analytics</Trans>
    ),
    grade: '9-12',
    activites: 200,
    lessons: 168,
    duration: '18-20',
    Age: '13-17',
    description: defineMessage({
      message:
        'Master the core principles of data science and gain practical experience through hands-on projects.',
    }),
    image: 'data-science',
    altText: 'Data Science Course for Teens with Python & Analytics Skills',
    outcomes: [
      defineMessage({ message: 'Master advanced libraries in Python' }),
      defineMessage({ message: 'Learn data structures and algorithms' }),
      defineMessage({ message: 'Analyze & interpret complex data sets' }),
      defineMessage({
        message: 'Implement machine learning and deep learning projects',
      }),
    ],
    courseCode: 'ds',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/data-science.pdf',
    trialCourseQuery: 'data-science-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.dataScience,
  },
  {
    title: <Trans>IOI Algorithms: Master Algorithms with Python</Trans>,
    titleCompactView: (
      <Trans>IOI Algorithms: Master Algorithms with Python</Trans>
    ),
    grade: '9-12',
    activites: 150,
    lessons: 120,
    duration: '12-14',
    Age: '13-17',
    description: defineMessage({
      message:
        'Develop advanced skills in data structures and algorithms through hands-on problem-solving.',
    }),
    image: 'ioi-algorithms',
    altText: 'IOI Algorithms Course for Teens to Master Algorithms with Python',
    outcomes: [
      defineMessage({ message: 'Master data structures and algorithms' }),
      defineMessage({ message: 'Master problem-solving' }),
      defineMessage({ message: 'Enhance Python programming skills' }),
      defineMessage({ message: 'Prepare for coding competitions' }),
    ],
    courseCode: 'ioi',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ioi-algorithms.pdf',
    trialCourseQuery: 'algorithms',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.IoiAlgorithms,
  },
  {
    title: <Trans>AP Computer Science A: Programming with Java</Trans>,
    titleCompactView: (
      <Trans>AP Computer Science A: Programming with Java</Trans>
    ),
    grade: '11-12',
    activites: 70,
    lessons: 48,
    duration: '4-5',
    Age: '15-17',
    description: defineMessage({
      message:
        'Master Java programming, OOP, data structures, and algorithms through hands-on problem-solving.',
    }),
    image: 'ap-computer-science-a',
    altText:
      'AP Computer Science A Course for Teens to Learn Programming with Java',
    outcomes: [
      defineMessage({ message: 'Master Java programming' }),
      defineMessage({ message: 'Understand & apply OOP concepts' }),
      defineMessage({ message: 'Enhance algorithmic thinking' }),
      defineMessage({ message: 'Develop advanced problem-solving skills' }),
    ],
    courseCode: 'apcsa',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ap-computer-science-a.pdf',
    trialCourseQuery: 'ap-computer-science-a-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.APComputerScienceA,
  },
  {
    title: <Trans>AI Genius - ChatGPT, AI-powered Apps & Games</Trans>,
    titleCompactView: (
      <Trans>AI Genius - ChatGPT, AI-powered Apps & Games</Trans>
    ),
    grade: '3-7',
    activites: 48,
    lessons: 48,
    duration: '4-6',
    Age: '7-14',
    description: defineMessage({
      message:
        'In this 48-lesson course, kids dive into AI, creating art, music, chatbots, and games, learning image, sound, pose recognition, and a bit of ChatGPT magic!',
    }),
    image: 'ai-genius',
    altText:
      'AI Genius - ChatGPT, AI-powered Apps & Games for Kids to Learn AI & Programming',
    outcomes: [
      defineMessage({ message: 'Build AI games and apps' }),
      defineMessage({ message: 'Make translators and speech tools' }),
      defineMessage({ message: 'Create carbon calculators' }),
      defineMessage({ message: 'Develop a chatbot with OpenAI' }),
    ],
    courseCode: 'aigc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ai-genius.pdf',
    trialCourseQuery: 'ai-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.aiAndMl,
  },
  {
    title: <Trans>AI Expert - Hands-On AI, LLMs & Python</Trans>,
    titleCompactView: <Trans>AI Expert - Hands-On AI, LLMs & Python</Trans>,
    grade: '8-12',
    activites: 48,
    lessons: 48,
    duration: '4-6',
    Age: '12-17',
    description: defineMessage({
      message:
        'In this 50-lesson advanced AI & ML course, students use LLMs and APIs to create projects like chatbots, translators, and image recognition tools.',
    }),
    image: 'ai-expert',
    altText:
      'AI Expert - Hands-On AI, LLMs & Python for Teens to Learn AI & Coding',
    outcomes: [
      defineMessage({ message: 'Learn image processing with OpenCV.' }),
      defineMessage({ message: 'Generate stunning AI images from text.' }),
      defineMessage({ message: 'Implement AI speech-to-text.' }),
      defineMessage({ message: 'Create text-to-speech generators.' }),
    ],
    courseCode: 'aiepc',
    curriculumPDFLink:
      'https://codingal.s3.ap-south-1.amazonaws.com/web/pdfs/curriculum/courses/v7/ai-expert.pdf',
    trialCourseQuery: 'ai-trial-class',
    isBestSeller: false,
    coursePageUrl: COURSE_PAGE_URL.aiAndMl,
  },
]

const Courses = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [coursePDFCode, setCoursePDFCode] = useState<string | null>('cp')
  const [gradeOptions, setGradeOptions] = useState<number[]>([
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
  ])
  const handlePDFDownload = (courseCode: string, gradeOptions: any) => {
    setCoursePDFCode(courseCode)
    setIsModalOpen(true)
    setGradeOptions(gradeOptions)
  }

  const getRandomHoverBg = () => {
    const randomIndex = Math.floor(Math.random() * HOVER_BG.length)
    return HOVER_BG[randomIndex]
  }

  getRandomHoverBg()

  return (
    <div className="bg-grey-100">
      <div
        className="container mx-auto flex flex-col space-y-10 leading-tight py-16 text-grey px-5"
        id="courses"
      >
        <CurriculumDownloadModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          grades={gradeOptions}
          curriculumPDFPrefix={coursePDFCode.toLowerCase()}
        />
        <div className="text-center">
          <h2 className="text-2xl lg:text-4xl font-600 mb-4">
            <Trans>
              Choose a <span className="underlined-text">coding course</span>{' '}
              that excites your child
            </Trans>
          </h2>
        </div>

        <div className="flex flex-wrap justify-center">
          {COURSE_INFO.map((course, i) => (
            <CourseCard
              key={i}
              course={course}
              onPDFDownload={handlePDFDownload}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Courses
